import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import Alert from '../../components/Alert/Alert';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import { getActivePeriodRange, getActiveFilterCount, getPeriodDateRange } from '../../components/common/AdvancedFilter/periodUtils';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import * as XLSX from 'xlsx';
import { formatDateDDMMYYYY } from '../../utils/formatters';
import { getDisplayedLeads, normalizeLeadRows } from './leadsReportUtils';

const DEFAULT_PAGE_SIZE = 10;

// Map frontend column keys to backend sort property names (Spring sort param) — backend-based
const LEADS_REPORT_SORT_KEY_TO_BACKEND = {
  projectName: 'projectName',
  companyName: 'companyName',
  source: 'source',
  tagging: 'tagging',
  stage: 'stage',
  pic: 'pic',
  email: 'email',
  sector: 'sector',
  createdBy: 'createdBy',
  department: 'department',
  assignedTo: 'assignedTo',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  partnerType: 'partnerType',
  proposalStatus: 'hasProposal',
  taskUpdateDate: 'taskUpdateDate',
  taskUpdateBy: 'taskUpdateBy',
};

// Serialize filter value (array or string) to API param: "all" or "id1,id2,..."
const toFilterParam = (v) => {
  if (Array.isArray(v)) {
    const clean = v.filter((x) => x != null && x !== '' && x !== 'all');
    return clean.length === 0 ? 'all' : clean.join(',');
  }
  return v == null || v === '' ? 'all' : String(v);
};

const emptyLeadsReportFilters = () => ({
  period: '',
  startDate: '',
  endDate: '',
  createdBy: [],
  assignedTo: [],
  department: [],
  source: [],
  sector: [],
  standard: [],
  service: [],
  branchId: '',
  taggingIds: [],
  proposalConverted: '',
  leadUpdateFrom: '',
  leadUpdateTo: '',
  taskUpdateFrom: '',
  taskUpdateTo: '',
});

const LeadsReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loadPersistedState, persistState } = usePersistedFilters('leads_report');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [leadsData, setLeadsData] = useState([]);

  // Server-side pagination (backend returns Page<T>)
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Server-side sort (ascending/descending handled by backend)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();

  // Helper function to check leads-specific capabilities
  const canLeads = (capabilityKey) => can(capabilityKey, 'leads');

  // TASK UPDATE PERMISSION: Check if user can view task update date
  const canViewTaskUpdate = can('canviewtaskupdate', 'task');

  // Unified filter state: filterData (in-progress) + appliedFilters (committed)
  const [filterData, setFilterData] = useState(emptyLeadsReportFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyLeadsReportFilters);

  // Track if filters were initialized from URL to prevent overrides
  const [filtersInitializedFromUrl, setFiltersInitializedFromUrl] = useState(false);
  // Track if URL params have been processed to prevent loading before URL params are read
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  // Filter options state
  const [taggingOptions, setTaggingOptions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sourcesData, setSourcesData] = useState([]);
  const [businessUnitsData, setBusinessUnitsData] = useState([]);
  const [sectorsData, setSectorsData] = useState([]);
  const [standardsData, setStandardsData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [createdByUsersData, setCreatedByUsersData] = useState([]);
  const [assignedToUsersData, setAssignedToUsersData] = useState([]);
  const [assignedToDepartmentsData, setAssignedToDepartmentsData] = useState([]);
  // HRIS user status lookup for "(Inactive)" badge
  const [leadsRptUserStatusMap, setLeadsRptUserStatusMap] = useState(new Map());
  useEffect(() => {
    api.get('/md/internal-users/option-internal-user')
      .then(res => {
        const map = new Map();
        (res.data || []).forEach(u => { if (u.id != null) map.set(String(u.id), u.status); });
        setLeadsRptUserStatusMap(map);
      })
      .catch(() => {});
  }, []);

  // Determine which filters to show based on permissions
  const getEnabledFilters = () => {
    const baseFilters = [];

    if (canLeads('view_global')) {
      baseFilters.push('createdBy', 'assignedTo', 'department', 'source', 'sector', 'standard', 'service', 'period', 'leadUpdatePeriod');
    } else if (canLeads('view_department')) {
      baseFilters.push('createdBy', 'assignedTo', 'department', 'source', 'sector', 'standard', 'service', 'period', 'leadUpdatePeriod');
    } else if (canLeads('view_own')) {
      baseFilters.push('source', 'sector', 'standard', 'service', 'period', 'leadUpdatePeriod');
    } else {
      baseFilters.push('source', 'sector', 'standard', 'service', 'period', 'leadUpdatePeriod');
    }

    if (canViewTaskUpdate) {
      baseFilters.push('taskUpdatePeriod');
    }

    baseFilters.push('branch', 'tagging');

    return baseFilters;
  };

  // Get filter overrides for view_department users
  const getFilterOverrides = () => {
    if (canLeads('view_department') && !canLeads('view_global')) {
      return {
        filterPeopleByDepartment: user?.departmentId ? String(user.departmentId) : null
      };
    }
    return {};
  };

  // Fetch filter options
  const fetchSourcesData = async () => {
    try {
      const response = await api.get('/dashboard/sources');
      if (response.data && response.data.sources) {
        setSourcesData(response.data.sources);
      }
    } catch (error) {
      console.error('Error fetching sources data:', error);
      setSourcesData([]);
    }
  };

  const fetchStandardsData = async () => {
    try {
      const response = await api.get('/dashboard/standards');
      if (response.data && response.data.standards) {
        setStandardsData(response.data.standards);
      }
    } catch (error) {
      console.error('Error fetching standards data:', error);
      setStandardsData([]);
    }
  };

  const fetchBusinessUnitsData = async () => {
    try {
      const response = await api.get('/md/departement');
      setBusinessUnitsData((response.data || []).map(d => ({ id: d.id, name: d.name })));
    } catch (error) {
      console.error('Error fetching business units data:', error);
      setBusinessUnitsData([]);
    }
  };

  const fetchServicesData = async () => {
    try {
      const response = await api.get('/crm/services/options');
      setServicesData((response.data || []).map(s => ({ id: s.id ?? s.value, name: s.name ?? s.label })));
    } catch (error) {
      console.error('Error fetching services data:', error);
      setServicesData([]);
    }
  };

  const fetchSectorsData = async () => {
    try {
      const response = await api.get('/dashboard/sectors');
      if (response.data && response.data.sectors) {
        setSectorsData(response.data.sectors);
      }
    } catch (error) {
      console.error('Error fetching sectors data:', error);
      setSectorsData([]);
    }
  };

  const fetchCreatedByUsersData = async () => {
    try {
      const response = await api.get('/crm/leads/filter-options');
      if (response.data && response.data.createdByUsers) {
        let users = (response.data.createdByUsers || []).map(u => ({
          id: u.id,
          value: String(u.id),
          name: u.name,
          label: u.name,
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          departmentId: u.departmentId
        }));

        const overrides = getFilterOverrides();
        if (overrides.filterPeopleByDepartment) {
          users = users.filter(u => {
            const deptId = u.departmentId;
            return deptId !== null && deptId !== undefined &&
                   String(deptId) === String(overrides.filterPeopleByDepartment);
          });
        }

        setCreatedByUsersData(users);
      }
    } catch (error) {
      console.error('Error fetching created by users data:', error);
      setCreatedByUsersData([]);
    }
  };

  const fetchAssignedToUsersData = async () => {
    try {
      const response = await api.get('/crm/leads/filter-options');
      if (response.data && response.data.assignedToUsers) {
        let users = (response.data.assignedToUsers || []).map(u => ({
          id: u.id,
          value: String(u.id),
          name: u.name,
          label: u.name,
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          departmentId: u.departmentId
        }));

        const overrides = getFilterOverrides();
        if (overrides.filterPeopleByDepartment) {
          users = users.filter(u => {
            const deptId = u.departmentId;
            return deptId !== null && deptId !== undefined &&
                   String(deptId) === String(overrides.filterPeopleByDepartment);
          });
        }

        setAssignedToUsersData(users);
      } else {
        setAssignedToUsersData([]);
      }
    } catch (error) {
      console.error('Error fetching assigned to users data:', error);
      setAssignedToUsersData([]);
    }
  };

  // Load filter options on mount
  useEffect(() => {
    const enabledFilters = getEnabledFilters();
    if (enabledFilters.includes('source')) fetchSourcesData();

    api.get('/md/tagging', { params: { moduleKey: 'companies' } })
      .then(r => setTaggingOptions(r.data || []))
      .catch(() => {});
    if (user?.id) {
      api.get(`/user-branches/user/${user.id}`)
        .then(r => { const d = r.data?.data ?? r.data ?? []; setBranches(Array.isArray(d) ? d : []); })
        .catch(() => {});
    }

    fetchBusinessUnitsData();
    fetchSectorsData();
    fetchStandardsData();
    if (enabledFilters.includes('service')) fetchServicesData();

    if (enabledFilters.includes('createdBy')) {
      fetchCreatedByUsersData();
    }
    if (enabledFilters.includes('assignedTo')) {
      fetchAssignedToUsersData();
    }
  }, [user?.departmentId, canViewTaskUpdate]);

  // Extract combined departments from createdBy and assignedTo users after businessUnitsData is loaded
  useEffect(() => {
    const enabledFilters = getEnabledFilters();
    if (!enabledFilters.includes('department')) {
      setAssignedToDepartmentsData([]);
      return;
    }

    if (businessUnitsData.length > 0 && (createdByUsersData.length > 0 || assignedToUsersData.length > 0)) {
      const departmentMap = new Map();

      createdByUsersData.forEach(u => {
        if (u.departmentId) {
          if (!departmentMap.has(u.departmentId)) {
            const department = businessUnitsData.find(dept =>
              String(dept.id) === String(u.departmentId)
            );
            if (department) {
              departmentMap.set(u.departmentId, {
                id: u.departmentId,
                value: String(u.departmentId),
                name: department.name,
                label: department.name
              });
            }
          }
        }
      });

      assignedToUsersData.forEach(u => {
        if (u.departmentId) {
          if (!departmentMap.has(u.departmentId)) {
            const department = businessUnitsData.find(dept =>
              String(dept.id) === String(u.departmentId)
            );
            if (department) {
              departmentMap.set(u.departmentId, {
                id: u.departmentId,
                value: String(u.departmentId),
                name: department.name,
                label: department.name
              });
            }
          }
        }
      });

      const departments = Array.from(departmentMap.values()).sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
      setAssignedToDepartmentsData(departments);
    } else {
      if (assignedToDepartmentsData.length > 0) {
        setAssignedToDepartmentsData([]);
      }
    }
  }, [businessUnitsData, createdByUsersData, assignedToUsersData]);

  // Initialize filters from URL query parameters (runs first)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasUrlParams = Array.from(searchParams.keys()).length > 0;

    if (hasUrlParams) {
      const parseParam = (key, altKey) => {
        const raw = searchParams.get(key) || searchParams.get(altKey || '') || '';
        if (!raw || raw === 'all') return [];
        return raw.split(',').map(s => s.trim()).filter(Boolean);
      };

      // Helper: resolve a dashboard period-key value to an explicit { from, to } date range.
      const resolvePeriodToRange = (periodVal, startKey, endKey) => {
        if (!periodVal || periodVal === 'all') return { from: '', to: '' };
        if (periodVal === 'custom') {
          return {
            from: searchParams.get(startKey) || '',
            to: searchParams.get(endKey) || '',
          };
        }
        const { startDate, endDate } = getPeriodDateRange(periodVal);
        return { from: startDate, to: endDate };
      };

      // Map URL params to unified filter state
      const periodParam = searchParams.get('period') || '';
      const customStartDate = searchParams.get('customStartDate') || searchParams.get('dateFrom') || '';
      const customEndDate = searchParams.get('customEndDate') || searchParams.get('dateTo') || '';

      // leadUpdatePeriod-keyed navigation (periodKey: 'leadUpdatePeriod')
      const leadUpdatePeriodKey = searchParams.get('leadUpdatePeriod') || '';
      const leadUpdateRange = resolvePeriodToRange(leadUpdatePeriodKey, 'leadUpdateStartDate', 'leadUpdateEndDate');

      // taskUpdatePeriod-keyed navigation (periodKey: 'taskUpdatePeriod')
      const taskUpdatePeriodKey = searchParams.get('taskUpdatePeriod') || '';
      const taskUpdateRange = resolvePeriodToRange(taskUpdatePeriodKey, 'taskUpdateStartDate', 'taskUpdateEndDate');

      const urlFilters = {
        period: periodParam === 'all' ? '' : periodParam,
        startDate: periodParam === 'custom' ? customStartDate : '',
        endDate: periodParam === 'custom' ? customEndDate : '',
        // Accept both report-native names and dashboard alias names
        createdBy: parseParam('createdBy'),
        department: parseParam('department', 'businessUnits'),
        // 'people' → assignedTo; 'taskUpdateBy' → assignedTo (task-update drill-down)
        assignedTo: (() => { const v = parseParam('assignedTo', 'people'); return v.length ? v : parseParam('taskUpdateBy'); })(),
        source: parseParam('source'),
        sector: parseParam('sector'),
        standard: parseParam('standard'),
        service: parseParam('service'),
        branchId: searchParams.get('branchId') || '',
        taggingIds: parseParam('taggingIds'),
        // Resolve leadUpdatePeriod key OR fall back to explicit date params
        leadUpdateFrom: leadUpdateRange.from || searchParams.get('leadUpdateStartDate') || searchParams.get('leadUpdateFrom') || '',
        leadUpdateTo: leadUpdateRange.to || searchParams.get('leadUpdateEndDate') || searchParams.get('leadUpdateTo') || '',
        // Resolve taskUpdatePeriod key OR fall back to explicit date params
        taskUpdateFrom: taskUpdateRange.from || searchParams.get('taskUpdateStartDate') || searchParams.get('taskUpdateFrom') || '',
        taskUpdateTo: taskUpdateRange.to || searchParams.get('taskUpdateEndDate') || searchParams.get('taskUpdateTo') || '',
      };

      setFilterData(urlFilters);
      setAppliedFilters(urlFilters);
      setFiltersInitializedFromUrl(true);
    } else {
      setFiltersInitializedFromUrl(false);
      // No URL params — restore from sessionStorage if available
      const saved = loadPersistedState();
      if (saved) {
        const restoredFilters = saved.filters ?? emptyLeadsReportFilters;
        const restoredSearch = saved.search ?? '';
        const restoredPage = saved.page ?? 0;
        const restoredSize = saved.size ?? DEFAULT_PAGE_SIZE;
        setFilterData(restoredFilters);
        setAppliedFilters(restoredFilters);
        setSearchQuery(restoredSearch);
        setPage(restoredPage);
        setSize(restoredSize);
      }
    }

    setUrlParamsProcessed(true);
  }, [location.search]);

  // When appliedFilters change, reset to first page
  useEffect(() => {
    setPage(0);
  }, [appliedFilters]);

  // Reload data when capabilities/URL are ready, or when page/size/sort/search or filters change
  useEffect(() => {
    if (!capabilitiesLoading && urlParamsProcessed) {
      loadLeads(page, size, sortConfig);
    }
  }, [capabilitiesLoading, urlParamsProcessed, page, size, sortConfig, searchQuery, appliedFilters]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const getProposalStatusBadge = (status) => {
    const colors = {
      'Converted': 'success',
      'Not Converted': 'orange'
    };
    return `badge badge-outline text-${colors[status] || 'secondary'}`;
  };

  // Helper function to filter data by date period (client-side fallback)
  const filterByPeriod = (data) => {
    let filteredData = data;

    // Filter by created date period
    const { startDate: createdStart, endDate: createdEnd } = getActivePeriodRange(appliedFilters);
    if (createdStart || createdEnd) {
      filteredData = filteredData.filter(item => {
        const dateStr = item.createdAt || item.created_at || item.createdDate;
        if (!dateStr) return false;
        const dateToFilter = new Date(dateStr);
        if (isNaN(dateToFilter.getTime())) return false;
        if (createdStart) {
          const start = new Date(createdStart);
          if (dateToFilter < start) return false;
        }
        if (createdEnd) {
          const end = new Date(createdEnd);
          end.setHours(23, 59, 59, 999);
          if (dateToFilter > end) return false;
        }
        return true;
      });
    }

    // Filter by lead update date range
    if (appliedFilters.leadUpdateFrom || appliedFilters.leadUpdateTo) {
      filteredData = filteredData.filter(item => {
        const leadUpdateDateStr = item.updatedAt || item.udate || item.updated_at;
        if (!leadUpdateDateStr || leadUpdateDateStr === 'null' || leadUpdateDateStr === null) {
          return false;
        }
        const leadUpdateDate = new Date(leadUpdateDateStr);
        if (isNaN(leadUpdateDate.getTime())) return false;

        if (appliedFilters.leadUpdateFrom) {
          const from = new Date(appliedFilters.leadUpdateFrom);
          if (leadUpdateDate < from) return false;
        }
        if (appliedFilters.leadUpdateTo) {
          const to = new Date(appliedFilters.leadUpdateTo);
          to.setHours(23, 59, 59, 999);
          if (leadUpdateDate > to) return false;
        }
        return true;
      });
    }

    // Filter by task update date range (if enabled and has permission)
    if (canViewTaskUpdate && (appliedFilters.taskUpdateFrom || appliedFilters.taskUpdateTo)) {
      filteredData = filteredData.filter(item => {
        const dateStr = item.taskUpdateDate || item.task_update_date || item.latestTaskUpdateDate;
        if (!dateStr) return false;
        const dateToFilter = new Date(dateStr);
        if (isNaN(dateToFilter.getTime())) return false;

        if (appliedFilters.taskUpdateFrom) {
          const from = new Date(appliedFilters.taskUpdateFrom);
          if (dateToFilter < from) return false;
        }
        if (appliedFilters.taskUpdateTo) {
          const to = new Date(appliedFilters.taskUpdateTo);
          to.setHours(23, 59, 59, 999);
          if (dateToFilter > to) return false;
        }
        return true;
      });
    }

    // Filter by sector(s) (client-side)
    const sectorIds = Array.isArray(appliedFilters.sector)
      ? appliedFilters.sector.filter(x => x != null && x !== '' && x !== 'all')
      : (appliedFilters.sector && appliedFilters.sector !== 'all' ? [appliedFilters.sector] : []);
    if (sectorIds.length > 0 && sectorsData.length > 0) {
      const sectorNames = new Set(sectorIds.map(id => {
        const s = sectorsData.find(sec => String(sec.id) === String(id));
        return s ? String(s.name).toLowerCase().trim() : null;
      }).filter(Boolean));
      if (sectorNames.size > 0) {
        filteredData = filteredData.filter(item => {
          const itemSectorName = item.namaSector || item.sectorName || item.sector_name || item.sector;
          return itemSectorName && sectorNames.has(itemSectorName.toLowerCase().trim());
        });
      }
    }

    // Filter by proposal converted status (client-side fallback)
    if (appliedFilters.proposalConverted === 'converted') {
      filteredData = filteredData.filter(item => Boolean(item.hasProposal));
    } else if (appliedFilters.proposalConverted === 'not_converted') {
      filteredData = filteredData.filter(item => !item.hasProposal);
    }

    return filteredData;
  };

  const loadLeads = async (pageNum = page, pageSize = size, sortConfigOverride = null) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(pageNum));
      params.set('size', String(pageSize));

      // Serialize multiselect filters
      const serializedCreatedBy = toFilterParam(appliedFilters.createdBy);
      const serializedAssignedTo = toFilterParam(appliedFilters.assignedTo);
      const serializedDepartment = toFilterParam(appliedFilters.department);
      const serializedSource = toFilterParam(appliedFilters.source);
      const serializedSector = toFilterParam(appliedFilters.sector);
      const serializedStandard = toFilterParam(appliedFilters.standard);
      const serializedService = toFilterParam(appliedFilters.service);
      const serializedTaggingIds = toFilterParam(appliedFilters.taggingIds);

      if (serializedCreatedBy !== 'all') params.append('createdBy', serializedCreatedBy);
      if (serializedAssignedTo !== 'all') params.append('assignedTo', serializedAssignedTo);
      if (serializedDepartment !== 'all') params.append('department', serializedDepartment);
      if (serializedSource !== 'all') params.append('source', serializedSource);
      if (serializedSector !== 'all') params.append('sector', serializedSector);
      if (serializedStandard !== 'all') params.append('standards', serializedStandard);
      if (serializedService !== 'all') params.append('service', serializedService);
      if (serializedTaggingIds !== 'all') params.append('taggingIds', serializedTaggingIds);

      // Main created date range from period or custom dates — use the shared periodUtils
      // resolver (same one Leads/index.jsx uses) so both pages compute identical ranges.
      const createdRange = getActivePeriodRange(appliedFilters);
      if (createdRange.startDate) params.append('dateFrom', createdRange.startDate);
      if (createdRange.endDate) params.append('dateTo', createdRange.endDate);

      // Lead update date range
      if (appliedFilters.leadUpdateFrom) params.append('leadUpdateFrom', appliedFilters.leadUpdateFrom);
      if (appliedFilters.leadUpdateTo) params.append('leadUpdateTo', appliedFilters.leadUpdateTo);

      // Task update date range
      if (canViewTaskUpdate) {
        if (appliedFilters.taskUpdateFrom) params.append('taskUpdateFrom', appliedFilters.taskUpdateFrom);
        if (appliedFilters.taskUpdateTo) params.append('taskUpdateTo', appliedFilters.taskUpdateTo);
      }

      // Permission-based filtering hierarchy
      if (!canLeads('view_global')) {
        const hasPeopleFilter = serializedCreatedBy !== 'all' || serializedAssignedTo !== 'all';
        if (canLeads('view_department') && !hasPeopleFilter && user?.departmentId) {
          params.append('viewDepartmentId', user.departmentId);
        } else if (canLeads('view_own') && !hasPeopleFilter && user?.id) {
          params.append('viewOwnUserId', user.id);
        }
      }

      if (appliedFilters.branchId) params.append('branchId', appliedFilters.branchId);
      if (appliedFilters.proposalConverted === 'converted') params.append('hasProposal', 'true');
      else if (appliedFilters.proposalConverted === 'not_converted') params.append('hasProposal', 'false');
      if (searchQuery && searchQuery.trim()) params.append('search', searchQuery.trim());

      // Server-side sort
      const effectiveSort = sortConfigOverride ?? sortConfig;
      if (effectiveSort?.key && LEADS_REPORT_SORT_KEY_TO_BACKEND[effectiveSort.key]) {
        const backendSortProperty = LEADS_REPORT_SORT_KEY_TO_BACKEND[effectiveSort.key];
        params.set('sort', `${backendSortProperty},${effectiveSort.direction}`);
      }

      const hasFilters = [...params.keys()].some(k => k !== 'page' && k !== 'size' && k !== 'sort');
      const url = hasFilters ? `/crm/leads/filter?${params.toString()}` : `/crm/leads?${params.toString()}`;
      const response = await api.get(url);
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.content ?? data?.data ?? []);

      const isPagedResponse = data && typeof data.totalElements === 'number';
      if (isPagedResponse) {
        setTotalElements(data.totalElements);
        setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(data.totalElements / pageSize)));
      } else {
        setTotalElements(list.length);
        setTotalPages(1);
      }

      const dataToShow = getDisplayedLeads(list, isPagedResponse, filterByPeriod);

      setLeadsData(normalizeLeadRows(dataToShow));
    } catch (error) {
      console.error('Error loading leads:', error);
      showAlert('danger', 'Failed to load leads');
    } finally {
      setIsLoading(false);
    }
  };

  // Cascade: filter createdBy/assignedTo by selected department(s)
  const withLeadsRptInactive = (u) =>
    leadsRptUserStatusMap.get(u.value) === '0' ? { ...u, label: `${u.label} (Inactive)` } : u;

  const cascadedLeadsRptCreatedBy = useMemo(() => {
    return createdByUsersData.map(u => withLeadsRptInactive({ value: String(u.id), label: u.name || u.label }));
  }, [createdByUsersData, leadsRptUserStatusMap]);

  const cascadedLeadsRptAssignedTo = useMemo(() => {
    const selectedDepts = (filterData.department || []).map(String);
    const base = assignedToUsersData.map(u => withLeadsRptInactive({ value: String(u.id), label: u.name || u.label }));
    if (!selectedDepts.length) return base;
    return base.filter(u => {
      const deptId = assignedToUsersData.find(d => String(d.id) === u.value)?.departmentId;
      return deptId != null && selectedDepts.includes(String(deptId));
    });
  // eslint-disable-next-line
  }, [filterData.department, assignedToUsersData, leadsRptUserStatusMap]);

  // Filter field configuration for AdvancedFilterPanel
  const leadsReportFilterFields = useMemo(() => {
    const enabledFilters = getEnabledFilters();
    const fields = [];

    if (enabledFilters.includes('leadUpdatePeriod')) {
      fields.push({
        key: 'leadUpdateRange',
        label: 'Lead Update Date',
        type: 'dateRange',
        fromKey: 'leadUpdateFrom',
        toKey: 'leadUpdateTo',
      });
    }

    if (canViewTaskUpdate && enabledFilters.includes('taskUpdatePeriod')) {
      fields.push({
        key: 'taskUpdateRange',
        label: 'Task Update Date',
        type: 'dateRange',
        fromKey: 'taskUpdateFrom',
        toKey: 'taskUpdateTo',
      });
    }

    if (enabledFilters.includes('department')) {
      fields.push({
        key: 'department',
        label: 'Department',
        type: 'multiselect',
        options: assignedToDepartmentsData.map(d => ({ value: String(d.id), label: d.name })),
      });
    }

    if (enabledFilters.includes('createdBy')) {
      fields.push({
        key: 'createdBy',
        label: 'Created By',
        type: 'multiselect',
        options: cascadedLeadsRptCreatedBy,
      });
    }

    if (enabledFilters.includes('assignedTo')) {
      fields.push({
        key: 'assignedTo',
        label: 'Assigned To',
        type: 'multiselect',
        options: cascadedLeadsRptAssignedTo,
      });
    }

    if (enabledFilters.includes('source')) {
      fields.push({
        key: 'source',
        label: 'Source',
        type: 'multiselect',
        options: sourcesData.map(s => ({ value: String(s.id), label: s.name })),
      });
    }

    if (enabledFilters.includes('sector')) {
      fields.push({
        key: 'sector',
        label: 'Sector',
        type: 'multiselect',
        options: sectorsData.map(s => ({ value: String(s.id), label: s.name })),
      });
    }

    if (enabledFilters.includes('standard')) {
      fields.push({
        key: 'standard',
        label: 'Standard',
        type: 'multiselect',
        options: standardsData.map(s => ({ value: String(s.id), label: s.name })),
      });
    }

    if (enabledFilters.includes('service')) {
      fields.push({
        key: 'service',
        label: 'Service',
        type: 'multiselect',
        options: servicesData.map(s => ({ value: String(s.id), label: s.name })),
      });
    }

    if (enabledFilters.includes('branch')) {
      fields.push({
        key: 'branchId',
        label: 'Branch',
        type: 'select',
        options: (branches || []).map(b => ({ value: String(b.id), label: b.name || String(b.id) })),
      });
    }

    if (enabledFilters.includes('tagging')) {
      fields.push({
        key: 'taggingIds',
        label: 'Tagging',
        type: 'multiselect',
        options: (taggingOptions || []).map(t => ({ value: String(t.id), label: t.name || String(t.id) })),
      });
    }

    fields.push({
      key: 'proposalConverted',
      label: 'Converted to Proposal',
      type: 'select',
      options: [
        { value: 'converted', label: 'Converted' },
        { value: 'not_converted', label: 'Not Converted' },
      ],
    });

    return fields;
  }, [
    canViewTaskUpdate,
    createdByUsersData,
    assignedToUsersData,
    assignedToDepartmentsData,
    cascadedLeadsRptCreatedBy,
    cascadedLeadsRptAssignedTo,
    sourcesData,
    sectorsData,
    standardsData,
    servicesData,
    branches,
    taggingOptions,
  ]);

  // Active filter count for badge
  const activeFilterCount = getActiveFilterCount(appliedFilters, leadsReportFilterFields);

  // Build filter tags for FilterAwarenessBar
  const filterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({
        key: 'period',
        label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}`,
      });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({
        key: 'period',
        label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}`,
      });
    }

    leadsReportFilterFields.forEach(field => {
      if (field.type === 'dateRange') {
        const from = appliedFilters[field.fromKey];
        const to = appliedFilters[field.toKey];
        if (from || to) {
          tags.push({
            key: field.key,
            label: `${field.label}: ${[from, to].filter(Boolean).join(' - ')}`,
          });
        }
        return;
      }

      const value = appliedFilters[field.key];
      const isEmpty = value === '' || value === null || value === undefined ||
        (Array.isArray(value) && value.length === 0);
      if (isEmpty) return;

      if (Array.isArray(value)) {
        tags.push({ key: field.key, label: `${field.label}: ${value.length} selected` });
        return;
      }

      const option = (field.options || []).find(opt => String(opt.value) === String(value));
      tags.push({ key: field.key, label: `${field.label}: ${option ? option.label : value}` });
    });

    return tags;
  }, [appliedFilters, leadsReportFilterFields]);

  // Handle in-progress filter changes (not yet applied)
  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  // Apply filter: commit filterData to appliedFilters and trigger fetch
  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    setPage(0);
    persistState({ filters: applied, search: searchQuery, page: 0, size });
    // loadLeads will be triggered by the useEffect watching appliedFilters
  };

  // Reset all filters
  const handleFilterReset = () => {
    const empty = emptyLeadsReportFilters();
    setFilterData(empty);
    setAppliedFilters(empty);
    setPage(0);
    persistState({ filters: empty, search: '', page: 0, size });
    // loadLeads will be triggered by the useEffect watching appliedFilters
  };

  // Remove individual filter chip
  const handleChipRemove = (key) => {
    const periodKeys = ['period', 'startDate', 'endDate'];
    const keysToClear = periodKeys.includes(key) ? periodKeys : [key];

    const field = leadsReportFilterFields.find(f => f.key === key);
    const nextApplied = { ...appliedFilters };

    if (field && field.type === 'dateRange') {
      nextApplied[field.fromKey] = '';
      nextApplied[field.toKey] = '';
    } else {
      keysToClear.forEach(k => {
        const f = leadsReportFilterFields.find(ff => ff.key === k);
        if (f && f.type === 'multiselect') {
          nextApplied[k] = [];
        } else {
          nextApplied[k] = '';
        }
      });
    }

    setFilterData(prev => ({ ...prev, ...nextApplied }));
    setAppliedFilters(nextApplied);
    setPage(0);
    persistState({ filters: nextApplied, search: searchQuery, page: 0, size });
  };

  const handleExportExcel = () => {
    if (!canLeads('export_data')) {
      showAlert('danger', 'You do not have permission to export data.');
      return;
    }
    try {
      const dataToExport = searchQuery
        ? leadsData.filter(item =>
            item.projectName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.pic?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : leadsData;

      const excelData = dataToExport.map(row => {
        const baseData = {
          'Project Name': row.projectName || '',
          'Company Name': row.companyName || '',
          'Lead Source': row.sourceName || row.leadSource || row.source?.name || row.source || '-',
          'Stage': row.stage || '',
          'PIC': row.pic || '',
          'Partner Type': row.partnerType || '',
          'Standard': row.standardName || row.standard || '-',
          'Email': row.email || '',
          'Created By': `${row.createdBy?.firstName || ''} ${row.createdBy?.lastName || ''}`.trim() || '-',
          'Department': row.department?.name || 'N/A',
          'Assigned To': `${row.assignedTo?.firstName || ''} ${row.assignedTo?.lastName || ''}`.trim() || '-',
          'Proposal Status': Boolean(row.hasProposal) ? 'Converted' : 'Not Converted',
          'Created At': row.createdAt ? formatDateDDMMYYYY(row.createdAt) : '',
          'Lead Update Date': row.updatedAt ? formatDateDDMMYYYY(row.updatedAt) : '-'
        };

        if (canViewTaskUpdate) {
          baseData['Task Update'] = row.taskUpdateDate ? formatDateDDMMYYYY(row.taskUpdateDate) : '-';
          baseData['Task Update By'] = row.taskUpdateBy ? `${row.taskUpdateBy.firstName || ''} ${row.taskUpdateBy.lastName || ''}`.trim() : '-';
        }

        return baseData;
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const columnWidths = [
        { wch: 30 }, { wch: 30 }, { wch: 20 }, { wch: 15 }, { wch: 25 },
        { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 20 },
        { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 },
        ...(canViewTaskUpdate ? [{ wch: 15 }, { wch: 20 }] : [])
      ];
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads Report');

      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(workbook, `leads_report_${timestamp}.xlsx`);

      showAlert('success', 'Leads report exported to Excel successfully!');
    } catch (e) {
      console.error('Export Excel failed:', e);
      showAlert('danger', 'Failed to export leads report to Excel. Please try again.');
    }
  };

  // TASK UPDATE PERMISSION: Memoize columns to recalculate when permission changes
  const columns = useMemo(() => [
    {
      key: 'projectName',
      label: 'Project Name',
      className: 'text-truncate',
      render: (value, item) => (
        <span
          className="text-primary text-truncate d-inline-block"
          style={{
            cursor: 'pointer',
            maxWidth: '300px',
            width: '300px'
          }}
          onClick={() => navigate(`/leads/detail/${item.id}`)}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'companyName',
      label: 'Company Name',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '300px', width: '300px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'tagging',
      label: 'Tagging',
      className: 'text-truncate',
      render: (value, item) => {
        const raw = item.tagging
          ?? (item.taggings && Array.isArray(item.taggings) ? item.taggings.map(t => t.name).join(', ') : null)
          ?? item.company?.tagging
          ?? (item.company?.taggings && Array.isArray(item.company.taggings) ? item.company.taggings.map(t => t.name).join(', ') : null)
          ?? '';
        const rawStr = typeof raw === 'string' ? raw : '';
        const tags = rawStr ? rawStr.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (tags.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {tags.map((tag, i) => (
              <span
                key={i}
                className="badge bg-blue-lt text-wrap"
                title={tag}
                style={{ fontSize: '0.7rem' }}
              >
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'source',
      label: 'Lead Source',
      className: 'text-truncate',
      render: (value, item) => {
        const sourceValue = value || item.sourceName || item.leadSource || item.source?.name || item.source || '-';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '180px', width: '180px' }}
            title={sourceValue}
          >
            {sourceValue}
          </span>
        );
      }
    },
    {
      key: 'stage',
      label: 'Stage',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'pic',
      label: 'PIC',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'partnerType',
      label: 'Partner Type',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'standardName',
      label: 'Standard',
      className: 'text-truncate',
      render: (value, item) => {
        const standardValue = value || item.standardName || item.standard || '-';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={standardValue}
          >
            {standardValue}
          </span>
        );
      }
    },
    {
      key: 'email',
      label: 'Email',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'createdBy',
      label: 'Created By',
      className: 'text-truncate',
      sortValue: (item) => `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim(),
      render: (value, item) => {
        const fullName = `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '160px', width: '160px' }}
            title={fullName}
          >
            {fullName}
          </span>
        );
      }
    },
    {
      key: 'department',
      label: 'Department',
      className: 'text-truncate',
      sortValue: (item) => item.department?.name || 'N/A',
      render: (value, item) => {
        const deptName = item.department?.name || 'N/A';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={deptName}
          >
            {deptName}
          </span>
        );
      }
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      className: 'text-truncate',
      sortValue: (item) => `${item.assignedTo?.firstName || ''} ${item.assignedTo?.lastName || ''}`.trim(),
      render: (value, item) => {
        const fullName = `${item.assignedTo?.firstName || ''} ${item.assignedTo?.lastName || ''}`.trim();
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '160px', width: '160px' }}
            title={fullName}
          >
            {fullName}
          </span>
        );
      }
    },
    {
      key: 'proposalStatus',
      label: 'Proposal Status',
      className: 'text-truncate',
      sortValue: (item) => (item.hasProposal ? 1 : 0),
      render: (value, item) => {
        const converted = Boolean(item.hasProposal);
        const statusLabel = converted ? 'Converted' : 'Not Converted';
        return (
          <span
            className={`${getProposalStatusBadge(statusLabel)} text-truncate d-inline-block`}
            style={{ maxWidth: '120px', width: '120px' }}
            title={statusLabel}
          >
            {statusLabel}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'date',
      className: 'text-truncate',
      render: (value) => {
        const dateValue = value ? formatDateDDMMYYYY(value) : '';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'updatedAt',
      label: 'Lead Update Date',
      type: 'date',
      className: 'text-truncate',
      render: (value, item) => {
        const dateValue = value || item.udate || item.updated_at;
        if (!dateValue || dateValue === '-' || dateValue === 'null' || dateValue === null) return '-';
        try {
          const formattedDate = formatDateDDMMYYYY(dateValue);
          return (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: '150px', width: '150px' }}
              title={formattedDate}
            >
              {formattedDate}
            </span>
          );
        } catch (e) {
          return '-';
        }
      }
    },
    // TASK UPDATE PERMISSION: Only show column if user has permission
    ...(canViewTaskUpdate ? [
      {
        key: 'taskUpdateDate',
        label: 'Task Update',
        type: 'date',
        className: 'text-truncate',
        render: (value) => {
          if (!value || value === '-') return '-';
          try {
            const dateValue = formatDateDDMMYYYY(value);
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: '150px', width: '150px' }}
                title={dateValue}
              >
                {dateValue}
              </span>
            );
          } catch (e) {
            return value || '-';
          }
        }
      },
      {
        key: 'taskUpdateBy',
        label: 'Task Update By',
        className: 'text-truncate',
        sortValue: (item) => `${item.taskUpdateBy?.firstName || ''} ${item.taskUpdateBy?.lastName || ''}`.trim(),
        render: (value, item) => {
          const fullName = `${item.taskUpdateBy?.firstName || ''} ${item.taskUpdateBy?.lastName || ''}`.trim();
          if (!fullName) return '-';
          return (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: '160px', width: '160px' }}
              title={fullName}
            >
              {fullName}
            </span>
          );
        }
      }
    ] : [])
  ], [canViewTaskUpdate, capabilitiesLoading, navigate]);

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col-md-3">
          <h3 className="page-title m-0">Leads Reports</h3>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={leadsData}
                      columns={columns}
                      isLoading={isLoading}
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onSearchSubmit={(query) => {
                            setSearchQuery(query);
                            setPage(0);
                          }}
                          placeholder="Search leads..."
                        />
                      }
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasLeadsReport" activeCount={activeFilterCount} />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={canLeads('export_data') ? handleExportExcel : undefined}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Leads Report is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={filterTags.map(tag => tag.label).join(' · ')}
                          loading={isLoading}
                          activeCount={activeFilterCount}
                          filterTags={filterTags}
                          onRemoveFilter={handleChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const nextPage = newPage - 1;
                          setPage(nextPage);
                          persistState({ filters: appliedFilters, search: searchQuery, page: nextPage, size });
                          loadLeads(nextPage, size, sortConfig);
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({ filters: appliedFilters, search: searchQuery, page: 0, size: newSize });
                          loadLeads(0, newSize, sortConfig);
                        },
                      }}
                      onSortChange={(newSortConfig) => {
                        setSortConfig(newSortConfig);
                        setPage(0);
                        loadLeads(0, size, newSortConfig);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filter Panel */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasLeadsReport"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={leadsReportFilterFields}
      />
    </div>
  );
};

export default LeadsReports;
